if (!customElements.get('size-guide-modal')) {
  customElements.define(
    'size-guide-modal',
    class extends HTMLElement {
      constructor() {
        super();
        this.observedButton = null;
        this.observer = null;
        this.isAddingToCart = false;
        this.addToCartTimeout = null;
        this.onCartUpdate = this.handleCartUpdate.bind(this);
        this.onCartError = this.handleCartError.bind(this);
      }

      // ─── Lifecycle ────────────────────────────────────────────────────────────

      connectedCallback() {
        this.setupDOM();
        this.loadGuides();
        this.addEventListeners();
        this.syncSelectedVariantFromPicker();
        this.syncButtonState();
      }

      disconnectedCallback() {
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
          this.observedButton = null;
        }
        if (this.addToCartTimeout) {
          clearTimeout(this.addToCartTimeout);
          this.addToCartTimeout = null;
        }
        document.removeEventListener('cart:update', this.onCartUpdate, true);
        document.removeEventListener('cart:error', this.onCartError, true);
      }

      // hide add to bag on bundle products
      hasTwoFieldsets() {
        const form =
          this.closest('.shopify-section')?.querySelector('form.variant-picker__form') ||
          document.querySelector('form.variant-picker__form');
        return form ? form.querySelectorAll(':scope > fieldset').length === 2 : false;
      }

      // Cache dialog references and inject the guide-header and footer containers
      setupDOM() {
        this.dialog = this.querySelector('dialog');
        this.closeButton = this.querySelector('.close-button');

        this.guideHeader = document.createElement('div');
        this.guideHeader.classList.add('guide-header');
        this.dialog.prepend(this.guideHeader);

        // hide add to bag on bundle products
        if (!this.hasTwoFieldsets()) {
          const template = this.querySelector('#size-guide-footer-template, template');
          if (template) {
            const clone = template.content.cloneNode(true);
            this.dialog.appendChild(clone);
            this.footer = this.dialog.querySelector('.size-guide__footer');
          } else {
            this.footer = this.querySelector('.size-guide__footer');
          }

          this.atcButton = this.footer ? this.footer.querySelector('button') : null;
          if (this.atcButton) {
            this.atcButton.type = 'button';
          }
        }
      }

      // Read and parse the embedded JSON, then render guides
      // Expected JSON shape: [{ guideName: "Shoes", html: "<table>...</table>" }, ...]
      loadGuides() {
        const jsonScript = document.getElementById('size-guide-data');
        if (!jsonScript) return;

        try {
          const guides = JSON.parse(jsonScript.textContent);
          this.setGuides(guides);
        } catch (e) {
          console.error('Invalid size guide JSON:', e);
        }
      }

      // ─── Events ───────────────────────────────────────────────────────────────

      addEventListeners() {
        this.bindOpenClose();
        this.bindBackdropClose();
        this.bindAddToCart();
        this.bindObservers();
        document.addEventListener('cart:update', this.onCartUpdate, true);
        document.addEventListener('cart:error', this.onCartError, true);
      }

      // Wire the external open button and the in-dialog close button
      bindOpenClose() {
        document.addEventListener('click', (event) => {
          if (event.target.closest('#open-size-guide, .product__size-guide-button')) {
            this.setupObserver();
            this.syncSelectedVariantFromPicker();
            this.syncButtonState();
            this.dialog?.showModal();
          }
        });

        if (this.closeButton) {
          this.closeButton.addEventListener('click', () => this.dialog?.close());
        }

        if (this.dialog) {
          this.dialog.addEventListener('close', () => {
            if (this.addToCartTimeout) {
              clearTimeout(this.addToCartTimeout);
              this.addToCartTimeout = null;
            }
            this.isAddingToCart = false;
            this.atcButton?.removeAttribute('data-added');
            const realBtn = this.getRealAddToCartButton();
            if (realBtn) {
              realBtn.dataset.puppet = 'false';
            }
            this.syncButtonState();
          });
        }
      }

      // Close dialog on backdrop click (browser backdrop events are unreliable)
      bindBackdropClose() {
        this.dialog.addEventListener('click', (event) => {
          const rect = this.dialog.getBoundingClientRect();
          const clickedOutside =
            event.clientX < rect.left ||
            event.clientX > rect.right ||
            event.clientY < rect.top ||
            event.clientY > rect.bottom;

          if (clickedOutside) this.dialog.close();
        });

        // Prevent inner UI elements from bubbling clicks up to the backdrop handler
        this.dialog.querySelectorAll('.guide-header, .tab-header, .size-guide__footer').forEach((el) => {
          el.addEventListener('click', (e) => e.stopPropagation());
        });
      }

      bindAddToCart() {
        if (this.atcButton) {
          this.atcButton.addEventListener('click', (e) => this.handleAddToCartClick(e));
        }
      }

      bindObservers() {
        this.setupObserver();

        const section = this.closest('.shopify-section') || document;
        section.addEventListener('variant:update', () => {
          this.setupObserver();
          this.syncSelectedVariantFromPicker();
          this.syncButtonState();
        });

        section.addEventListener('variant:selected', () => {
          this.setupObserver();
          this.syncButtonState();
        });

        document.addEventListener('DOMContentLoaded', () => {
          this.setupObserver();
          this.syncButtonState();
        });
      }

      setupObserver() {
        if (!this.atcButton) return;
        const realBtn = this.getRealAddToCartButton();
        if (!realBtn) return;

        if (this.observedButton !== realBtn) {
          if (this.observer) this.observer.disconnect();
          this.observedButton = realBtn;
          this.observer = new MutationObserver(() => this.syncButtonState());
          this.observer.observe(realBtn, {
            attributes: true,
            childList: true,
            subtree: true,
            characterData: true,
          });
        }
      }

      // ─── Guide tabs (top-level) ───────────────────────────────────────────────

      // Render one or more top-level guides; only show switcher tabs when there are multiple
      setGuides(guides) {
        this.guideHeader.innerHTML = '';
        this.dialog.querySelectorAll('.guide-content').forEach((el) => el.remove());

        const hasMultiple = guides.length > 1;

        guides.forEach((guide, index) => {
          const wrapper = this.createGuideWrapper(index);
          this.setContent(guide.html, wrapper);

          if (hasMultiple) {
            const btn = this.createGuideButton(guide.guideName, index, wrapper);
            this.guideHeader.appendChild(btn);
          }
        });

        this.syncSelectedVariantFromPicker();
        this.syncButtonState();
      }

      // Create the content wrapper div for a single guide
      createGuideWrapper(index) {
        const wrapper = document.createElement('div');
        wrapper.classList.add('guide-content');
        if (index === 0) wrapper.classList.add('active');
        if (this.footer) {
          this.dialog.insertBefore(wrapper, this.footer);
        } else {
          this.dialog.appendChild(wrapper);
        }
        return wrapper;
      }

      // Create a top-level guide switcher button and wire its click handler
      createGuideButton(name, index, wrapper) {
        const btn = document.createElement('button');
        btn.textContent = name || `Size Guide ${index + 1}`;
        btn.dataset.guide = index;
        if (index === 0) btn.classList.add('active');

        btn.addEventListener('click', () => {
          this.activate(this.guideHeader, 'button', btn);
          this.activate(this.dialog, '.guide-content', wrapper);
        });

        return btn;
      }

      // ─── Inner tabs (per guide) ───────────────────────────────────────────────

      // Parse the guide HTML and build one tab per <table>
      setContent(htmlString, container) {
        const tables = this.parseTables(htmlString);

        const tabHeader = document.createElement('div');
        tabHeader.classList.add('tab-header');
        container.appendChild(tabHeader);

        tables.forEach((table, i) => {
          const { label, image, tableEl } = this.prepareTable(table, i);

          const tabBtn = this.createTabButton(label, i);
          tabHeader.appendChild(tabBtn);

          const tabPanel = this.createTabPanel(image, tableEl, i);
          container.appendChild(tabPanel);

          // Switch active tab on click
          tabBtn.addEventListener('click', () => {
            this.activate(tabHeader, 'button', tabBtn);
            this.activate(container, '.tab-content', tabPanel);
          });
        });
      }

      // Parse an HTML string and return all <table> elements
      parseTables(htmlString) {
        const doc = new DOMParser().parseFromString(htmlString, 'text/html');
        return Array.from(doc.querySelectorAll('table'));
      }

      // Clone a table, strip colgroup, extract the tab label from row 1 and
      // an optional image from row 2, then return the cleaned table element
      prepareTable(table, index) {
        const tableEl = table.cloneNode(true);
        tableEl.classList.add('size-guide-table');
        tableEl.querySelector('colgroup')?.remove();

        const rows = tableEl.querySelectorAll('tr');
        if (rows.length === 0) return { label: `Tab ${index + 1}`, image: null, tableEl };

        // Row 0: used only as the tab label — not rendered in the table body
        const labelCell = rows[0].querySelector('td, th');
        const label = labelCell ? labelCell.textContent.trim() : `Tab ${index + 1}`;
        rows[0].remove();

        // Row 1 (after removal): if it contains an image, lift it out as a separate element
        let image = null;
        const nextRow = tableEl.querySelectorAll('tr')[0];
        if (nextRow?.querySelector('img')) {
          image = nextRow.querySelector('img').cloneNode(true);
          nextRow.remove();
        }

        this.enhanceTableWithInteractivity(tableEl);

        return { label, image, tableEl };
      }

      enhanceTableWithInteractivity(tableEl) {
        if (this.hasTwoFieldsets()) return;

        const remainingRows = Array.from(tableEl.querySelectorAll('tr'));
        if (remainingRows.length <= 1) return;

        for (let r = 1; r < remainingRows.length; r++) {
          const row = remainingRows[r];
          const firstCell = row.cells ? row.cells[0] : row.querySelector('td, th');
          if (!firstCell) continue;

          const sizeText = firstCell.textContent?.trim();
          if (!sizeText) continue;

          firstCell.classList.add('size-guide-size-cell');
          firstCell.dataset.size = sizeText;
          row.dataset.size = sizeText;

          if (!this.isSizeAvailable(sizeText)) {
            firstCell.classList.add('is-sold-out');
            row.classList.add('is-sold-out');
          }

          row.addEventListener('click', () => {
            this.handleSizeClick(sizeText);
          });
        }
      }

      // Create a tab switcher button
      createTabButton(label, index) {
        const btn = document.createElement('button');
        btn.textContent = label;
        btn.dataset.tab = index;
        if (index === 0) btn.classList.add('active');
        return btn;
      }

      // Build the tab panel div, optionally prepending an image above the table
      createTabPanel(image, tableEl, index) {
        const panel = document.createElement('div');
        panel.classList.add('tab-content');
        if (index === 0) panel.classList.add('active');

        if (image) {
          const imgWrapper = document.createElement('div');
          imgWrapper.classList.add('size-guide-image');
          imgWrapper.appendChild(image);
          panel.appendChild(imgWrapper);
        }

        panel.appendChild(tableEl);
        return panel;
      }

      // ─── Size Matching & Synchronization ─────────────────────────────────────

      normalizeSize(str) {
        if (!str) return '';
        return str
          .toLowerCase()
          .replace(/^uk\s*/i, '')
          .replace(/[^a-z0-9.]/g, '')
          .trim();
      }

      getVariantPicker() {
        return this.closest('.shopify-section')?.querySelector('variant-picker') || document.querySelector('variant-picker');
      }

      getRealAddToCartButton() {
        const section = this.closest('.shopify-section') || document;
        return (
          section.querySelector('product-form-component [ref="addToCartButton"]') ||
          section.querySelector('product-form-component button[name="add"]') ||
          section.querySelector('product-form-component button[type="submit"]') ||
          document.querySelector('product-form-component [ref="addToCartButton"]') ||
          document.querySelector('product-form-component button[name="add"]') ||
          document.querySelector('.buy-buttons-block button[type="submit"]')
        );
      }

      findMatchingVariantInput(sizeText) {
        const variantPicker = this.getVariantPicker();
        if (!variantPicker) return null;

        const targetNorm = this.normalizeSize(sizeText);
        if (!targetNorm) return null;

        const inputs = Array.from(variantPicker.querySelectorAll('input[type="radio"]'));
        for (const input of inputs) {
          const valNorm = this.normalizeSize(input.value);
          const labelNorm = this.normalizeSize(input.getAttribute('aria-label') || '');
          const textNorm = this.normalizeSize(
            input.closest('label')?.querySelector('.variant-option__button-label__text')?.textContent || ''
          );

          if (valNorm === targetNorm || labelNorm === targetNorm || textNorm === targetNorm) {
            return input;
          }
        }

        const select = variantPicker.querySelector('select');
        if (select) {
          for (const opt of Array.from(select.options)) {
            if (
              this.normalizeSize(opt.value) === targetNorm ||
              this.normalizeSize(opt.textContent || '') === targetNorm
            ) {
              return opt;
            }
          }
        }

        return null;
      }

      isSizeAvailable(sizeText) {
        const match = this.findMatchingVariantInput(sizeText);
        if (!match) return false;
        if (
          match.dataset.optionAvailable === 'false' ||
          match.disabled ||
          match.getAttribute('aria-disabled') === 'true'
        ) {
          return false;
        }
        return true;
      }

      getKlaviyoTrigger() {
        const section = this.closest('.shopify-section') || document;
        return section.querySelector('.klaviyo-bis-trigger') || document.querySelector('.klaviyo-bis-trigger');
      }

      handleSizeClick(sizeText) {
        if (this.hasTwoFieldsets()) return;

        const match = this.findMatchingVariantInput(sizeText);
        if (match) {
          if (match instanceof HTMLOptionElement) {
            const select = match.closest('select');
            if (select) {
              select.value = match.value;
              select.dispatchEvent(new Event('change', { bubbles: true }));
            }
          } else {
            match.checked = true;
            match.dispatchEvent(new Event('change', { bubbles: true }));
          }

          // Optimistically update fake button based on clicked variant availability
          if (this.atcButton) {
            const isAvail = match.dataset.optionAvailable !== 'false' && !match.disabled;
            const fakeTextEl =
              this.atcButton.querySelector('.add-to-cart-text__content span span') ||
              this.atcButton.querySelector('.add-to-cart-text__content');
            const fakeIcon = this.atcButton.querySelector('.add-to-cart-icon');
            const klaviyoTrigger = this.getKlaviyoTrigger();

            if (isAvail) {
              this.atcButton.disabled = false;
              this.atcButton.removeAttribute('aria-disabled');
              if (fakeTextEl) fakeTextEl.textContent = 'ADD TO CART';
              if (fakeIcon) fakeIcon.style.display = '';
            } else {
              const notifyText = klaviyoTrigger?.textContent?.trim() || 'SOLD OUT';
              if (fakeTextEl) fakeTextEl.textContent = notifyText.toUpperCase();
              if (fakeIcon) fakeIcon.style.display = 'none';
              if (klaviyoTrigger) {
                this.atcButton.disabled = false;
                this.atcButton.removeAttribute('aria-disabled');
              } else {
                this.atcButton.disabled = true;
                this.atcButton.setAttribute('aria-disabled', 'true');
              }
            }
          }
        }

        this.highlightSelectedSize(sizeText);
        setTimeout(() => this.syncButtonState(), 100);
      }

      highlightSelectedSize(sizeText) {
        const targetNorm = this.normalizeSize(sizeText);
        this.dialog.querySelectorAll('.size-guide-table tr, .size-guide-size-cell').forEach((el) => {
          el.classList.remove('is-selected', 'button');
        });

        if (!targetNorm) return;

        this.dialog.querySelectorAll('.size-guide-size-cell').forEach((cell) => {
          if (this.normalizeSize(cell.dataset.size) === targetNorm) {
            cell.classList.add('is-selected', 'button');
            cell.closest('tr')?.classList.add('is-selected');
          }
        });
      }

      syncSelectedVariantFromPicker() {
        if (this.hasTwoFieldsets()) return;

        const variantPicker = this.getVariantPicker();
        if (!variantPicker) return;

        const checked = variantPicker.querySelector('input[type="radio"]:checked');
        if (checked) {
          const text =
            checked.closest('label')?.querySelector('.variant-option__button-label__text')?.textContent ||
            checked.value;
          this.highlightSelectedSize(text);
        } else {
          const select = variantPicker.querySelector('select');
          if (select && select.value) {
            const opt = select.options[select.selectedIndex];
            this.highlightSelectedSize(opt?.textContent || select.value);
          } else {
            this.highlightSelectedSize('');
          }
        }
      }

      syncButtonState() {
        if (!this.atcButton) return;
        if (this.isAddingToCart) return;

        this.setupObserver();

        const realBtn = this.getRealAddToCartButton();
        const variantPicker = this.getVariantPicker();
        const klaviyoTrigger = this.getKlaviyoTrigger();

        let realText = '';
        let isDisabled = true;
        let isAvailable = false;
        let isNotifyMe = false;

        // Check variant-picker for selection requirements and selected variant
        const isSelectionRequired = variantPicker?.hasAttribute('data-selection-required');
        const checkedRadio = variantPicker?.querySelector('input[type="radio"]:checked');
        const checkedSelect = variantPicker?.querySelector('select');
        const hasSelection = checkedRadio || (checkedSelect && checkedSelect.value && checkedSelect.value !== '');

        // Check if selected variant is out of stock
        const isSelectedOutOfStock = checkedRadio && (checkedRadio.dataset.optionAvailable === 'false' || checkedRadio.disabled);
        const buyButtonsBlock =
          document.querySelector('.product-details .buy-buttons-block') ||
          this.closest('.shopify-section')?.querySelector('.buy-buttons-block');
        const isBuyButtonsHidden = buyButtonsBlock?.classList.contains('hidden');
        const isKlaviyoVisible = klaviyoTrigger && (klaviyoTrigger.style.display !== 'none' || isBuyButtonsHidden);

        if (isSelectionRequired && !hasSelection) {
          const optionsCount = parseInt(variantPicker?.dataset.optionsCount || '1', 10);
          realText = optionsCount > 1 ? 'Select all options first' : 'Select a size';
          isDisabled = true;
        } else if (isSelectedOutOfStock || isKlaviyoVisible) {
          if (klaviyoTrigger) {
            realText = klaviyoTrigger.textContent?.trim() || 'Notify Me When Available';
            isDisabled = false;
            isNotifyMe = true;
          } else {
            realText = 'Sold out';
            isDisabled = true;
          }
        } else if (realBtn) {
          isDisabled = realBtn.disabled || realBtn.getAttribute('aria-disabled') === 'true';

          const realTextEl =
            realBtn.querySelector('.add-to-cart-text__content span span') ||
            realBtn.querySelector('.add-to-cart-text__content') ||
            realBtn.querySelector('.add-to-cart-text');

          realText = realTextEl ? realTextEl.textContent.trim() : 'Add to cart';
        } else {
          realText = 'Add to cart';
          isDisabled = false;
        }

        isAvailable = !isDisabled && !isNotifyMe && realText.toLowerCase().includes('add to cart');

        // Apply disabled state
        if (isDisabled) {
          this.atcButton.disabled = true;
          this.atcButton.setAttribute('aria-disabled', 'true');
        } else {
          this.atcButton.disabled = false;
          this.atcButton.removeAttribute('aria-disabled');
        }

        // Apply text
        const fakeTextEl =
          this.atcButton.querySelector('.add-to-cart-text__content span span') ||
          this.atcButton.querySelector('.add-to-cart-text__content') ||
          this.atcButton.querySelector('.add-to-cart-text');

        if (fakeTextEl && realText) {
          fakeTextEl.textContent = realText.toUpperCase();
        }

        // Sync Add to Cart icon
        let fakeIcon = this.atcButton.querySelector('.add-to-cart-icon');
        if (isAvailable) {
          if (!fakeIcon) {
            const realIcon = realBtn?.querySelector('.add-to-cart-icon');
            if (realIcon) {
              const textWrapper = this.atcButton.querySelector('.add-to-cart-text');
              if (textWrapper) textWrapper.prepend(realIcon.cloneNode(true));
            }
          } else {
            fakeIcon.style.display = '';
          }
        } else if (fakeIcon) {
          fakeIcon.style.display = 'none';
        }
      }

      /**
       * Handles add to cart clicks from within the size guide.
       * Displays the checkmark burst animation and delegates submission to the main product form.
       */
      handleAddToCartClick(event) {
        if (event) event.preventDefault();
        if (this.atcButton?.disabled || this.isAddingToCart) return;

        const klaviyoTrigger = this.getKlaviyoTrigger();
        const buyButtonsBlock =
          document.querySelector('.product-details .buy-buttons-block') ||
          this.closest('.shopify-section')?.querySelector('.buy-buttons-block');
        const isKlaviyoActive = klaviyoTrigger && (klaviyoTrigger.style.display !== 'none' || buyButtonsBlock?.classList.contains('hidden'));

        if (isKlaviyoActive) {
          klaviyoTrigger.click();
          this.dialog?.close();
          return;
        }

        const realBtn = this.getRealAddToCartButton();
        if (realBtn && !realBtn.disabled) {
          this.isAddingToCart = true;
          this.atcButton.dataset.added = 'true';
          const atcComponent = this.atcButton.closest('add-to-cart-component');
          if (atcComponent && typeof atcComponent.animateAddToCart === 'function') {
            atcComponent.animateAddToCart();
          }

          // Safety timeout: reset button state if no cart event responds within 8 seconds
          if (this.addToCartTimeout) clearTimeout(this.addToCartTimeout);
          this.addToCartTimeout = setTimeout(() => {
            if (this.isAddingToCart) {
              this.handleCartError();
            }
          }, 8000);

          realBtn.dataset.puppet = 'true';
          realBtn.click();
        }
      }

      /**
       * Handles successful cart addition.
       * Closes the size guide modal before the cart drawer is displayed.
       */
      handleCartUpdate(event) {
        if (!this.isAddingToCart) return;

        if (this.addToCartTimeout) {
          clearTimeout(this.addToCartTimeout);
          this.addToCartTimeout = null;
        }

        const didError = event.detail?.data?.didError;
        if (didError) {
          this.handleCartError();
          return;
        }

        // Close modal during capture phase so it dismisses before the cart drawer opens
        this.dialog?.close();
        this.isAddingToCart = false;
        this.atcButton?.removeAttribute('data-added');

        const realBtn = this.getRealAddToCartButton();
        if (realBtn) {
          realBtn.dataset.puppet = 'false';
        }
      }

      /**
       * Handles cart addition failures or timeouts, restoring the button state.
       */
      handleCartError() {
        if (this.addToCartTimeout) {
          clearTimeout(this.addToCartTimeout);
          this.addToCartTimeout = null;
        }
        this.isAddingToCart = false;
        this.atcButton?.removeAttribute('data-added');
        const realBtn = this.getRealAddToCartButton();
        if (realBtn) {
          realBtn.dataset.puppet = 'false';
        }
        this.syncButtonState();
      }

      // ─── Utility ─────────────────────────────────────────────────────────────

      // Remove 'active' from all matching children inside scope, then add it to target
      activate(scope, itemSelector, target) {
        scope.querySelectorAll(itemSelector).forEach((el) => el.classList.remove('active'));
        target.classList.add('active');
        this.syncSelectedVariantFromPicker();
      }
    }
  );
}
